// Left-rail bucket definitions for the Service Desk (new tab that will replace
// Service Journey). Connected buckets: Pending Invites, Customer Not Joined,
// Job Card Opened, Job Card Closed, Escalations.

import { CalendarClock, UserPlus, UserX, MessageSquare, CheckCircle2, FileText, AlertTriangle, Inbox } from "lucide-react";
import { nb, isEscalationFlagged, isWaGroupDeleted, resolveJourney } from "../../lib/journey";
import { customerInGroup, joinStatusFromGroup } from "../../lib/members";
import { buildEscalations } from "../../lib/queueMetrics";

export const norm = (v) => String(v ?? "").replace(/\s+/g, " ").trim().toLowerCase();

export const last10 = (v) => {
  const d = String(v ?? "").replace(/\D/g, "");
  return d.length > 10 ? d.slice(-10) : d;
};

// A group row has moved past "needs initiation" once Chat360 has actually made
// the WhatsApp group (or at least handed back an invite link).
export function isGroupInitiated(row) {
  return row["Status"] === "Created" || nb(row["Group ID"]) || nb(row["Invite Link"]);
}

// A booking whose WhatsApp group is still being made — queued ("Create") or
// waiting on Chat360 ("Pending Sync"). There is no chat to open yet, but it
// should not vanish from the desk meanwhile: the advisor has just booked it.
export function isGroupCreating(row) {
  return !isGroupInitiated(row) && ["Create", "Pending Sync"].includes(row["Status"]);
}

// The service advisor on `row` is the logged-in user — matched on phone first
// (most reliable), name second. `me` is the real auth identity, not the demo
// identity switcher.
export function isMyAssignedGroup(row, me) {
  if (!me) return false;
  const myNum = last10(me.phone);
  const myName = norm(me.name);
  return (
    (!!myNum && last10(row["Service Advisor Number"]) === myNum) ||
    (!!myName && myName === norm(row["Service Advisor Name"]))
  );
}

// One advisor across groups: matched on phone (last 10 digits), name when a
// group has no advisor number. "" when the group has no advisor at all.
export function advisorKey(row) {
  const num = last10(row?.["Service Advisor Number"]);
  if (num) return num;
  const name = norm(row?.["Service Advisor Name"]);
  return name ? `name:${name}` : "";
}

// The advisors who have groups in `rows`, for the desk's advisor filter,
// sorted by name. Each is listed once even if their name is spelt
// differently on some groups.
export function advisorOptions(rows) {
  const byKey = new Map();
  for (const row of rows || []) {
    const key = advisorKey(row);
    if (!key || byKey.has(key)) continue;
    const name = String(row["Service Advisor Name"] || "").trim();
    byKey.set(key, { key, label: name || String(row["Service Advisor Number"] || "").trim() });
  }
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export function matchesAdvisor(row, advisorFilter) {
  return advisorFilter === "all" || (!!row && advisorKey(row) === advisorFilter);
}

// Whether the logged-in user still needs to join this group. Uses their real
// auth identity (name + phone), not the demo identity switcher.
export function currentUserNotJoined(row, me) {
  if (!me) return false;
  return joinStatusFromGroup(row, "Service Advisor", me.name, me.phone) !== "Joined";
}

// Legacy name kept for tests — checks the SA on the row, not necessarily `me`.
export function serviceAdvisorNotJoined(row) {
  return (
    joinStatusFromGroup(
      row,
      "Service Advisor",
      row["Service Advisor Name"],
      row["Service Advisor Number"]
    ) !== "Joined"
  );
}

// Whether the customer specifically (not any other role) hasn't joined this
// group's WhatsApp group yet. Distinct from the generic "N members pending"
// count on a group card — a card can have zero pending stakeholders while
// the one person the group exists for hasn't joined, and that's the more
// urgent fact to lead with. A customer who joined with their second number
// has joined (customerInGroup).
export function customerNotJoined(row) {
  const altNumber = row["Alternate Customer Number"];
  return (
    joinStatusFromGroup(row, "Customer", row["Customer Name"], row["Customer Number"]) !== "Joined" &&
    (!nb(altNumber) || joinStatusFromGroup(row, "Alt Customer", row["Customer Name"], altNumber) !== "Joined")
  );
}

// The queues below that ask someone to act (invite, reply) skip a group whose
// WhatsApp side was deleted: there is nothing left to do in it. It still
// shows under All Conversations, read-only.

// Pending Invites — initiated groups where someone still needs to join.
// SA: only my assigned groups where I haven't joined yet.
// Master: every initiated group (API already returns all rows) where the
// assigned SA on that group hasn't joined — master is never assigned to groups.
export function pendingInvitesForMe(rows, me) {
  if (!me) return [];
  return (rows || []).filter((row) => {
    if (!isGroupInitiated(row) || isWaGroupDeleted(row)) return false;
    if (me.role === "master") return serviceAdvisorNotJoined(row);
    if (me.role === "sa") return isMyAssignedGroup(row, me) && currentUserNotJoined(row, me);
    return false;
  });
}

// Customer Not Joined — initiated groups where the customer specifically
// (not a staff member or stakeholder) hasn't joined yet. Same role scoping
// as every other queue: an advisor only sees their own assigned groups,
// everyone else sees whatever the API already returned for their workshop.
export function customerNotJoinedForMe(rows, me) {
  if (!me) return [];
  return (rows || []).filter((row) => {
    if (!isGroupInitiated(row) || isWaGroupDeleted(row)) return false;
    if (me.role === "sa" && !isMyAssignedGroup(row, me)) return false;
    return customerNotJoined(row);
  });
}

// Unanswered Messages — groups where the customer's latest message is still
// waiting for a reply. Which groups those are comes from the server (open reply
// clocks), so it arrives as `extras.awaitingReplyIds`; with nothing yet, the
// queue is empty rather than wrong. Scoped like every other queue.
export function unansweredForMe(rows, me, extras) {
  const waiting = extras?.awaitingReplyIds;
  if (!me || !waiting || waiting.size === 0) return [];
  return (rows || []).filter((row) => {
    if (!isGroupInitiated(row) || isWaGroupDeleted(row)) return false;
    if (me.role === "sa" && !isMyAssignedGroup(row, me)) return false;
    return waiting.has(row._row);
  });
}

// Job Card Opened / Closed — same journey fields the Service Journey stepper
// already uses (STEP_DEFS in lib/journey.js): "opened" is the job card send
// ("jobcard" stage, g["Job Card Sent"] === "Sent"), "closed" is the vehicle
// being delivered back to the customer ("vehicle_delivered" stage,
// nb(g["Delivery Notified"])) -- the journey's own definition of done. The
// two are mutually exclusive: once delivered, a job card no longer counts as
// open even though "Job Card Sent" stays "Sent" on the row.
export function jobCardOpenedForMe(rows, me) {
  if (!me) return [];
  return (rows || []).filter((row) => {
    if (!isGroupInitiated(row)) return false;
    if (me.role === "sa" && !isMyAssignedGroup(row, me)) return false;
    return row["Job Card Sent"] === "Sent" && !nb(row["Delivery Notified"]);
  });
}

export function jobCardClosedForMe(rows, me) {
  if (!me) return [];
  return (rows || []).filter((row) => {
    if (!isGroupInitiated(row)) return false;
    if (me.role === "sa" && !isMyAssignedGroup(row, me)) return false;
    return nb(row["Delivery Notified"]);
  });
}

// All Conversations — every group that exists as a WhatsApp group, whatever
// state it is in, plus bookings whose group is still being created (shown
// greyed out, see GroupCard). The other buckets are all queues: they answer "what needs me
// next", and a group that needs nothing right now falls out of every one of
// them and becomes unreachable from the desk. This is the bucket you open to
// find a specific vehicle and just talk to the customer.
//
// Scoped the same way the queues are: an advisor sees the job cards assigned to
// them, everyone else sees what the API already returned for their workshop.
export function allConversationsForMe(rows, me) {
  if (!me) return [];
  return (rows || []).filter((row) => {
    if (!isGroupInitiated(row) && !isGroupCreating(row)) return false;
    if (me.role === "sa") return isMyAssignedGroup(row, me);
    return true;
  });
}

// Groups with classifier escalation flag, scoped by role (for quick counts).
export function escalatedGroupsForMe(rows, me) {
  if (!me) return [];
  return (rows || []).filter((row) => {
    if (!isEscalationFlagged(row["Escalation Flag"])) return false;
    if (me.role === "master") return true;
    if (me.role === "sa") return isMyAssignedGroup(row, me);
    return false;
  });
}

// Full escalation incidents for the desk list — same source as /escalations page.
export function deskEscalationsForMe(rows, me, sentimentRows) {
  const scopedIds = new Set(escalatedGroupsForMe(rows, me).map((r) => r._row));
  return buildEscalations(rows || [], sentimentRows || []).filter((e) => scopedIds.has(e.groupRowId));
}

// `stageOverlay` is the tenant terminology overlay; omitted, the built-in
// stage names are used. `featureFlags` gates Hyundai-only stages.
export function escalationStageLabel(groupRow, stageOverlay, featureFlags) {
  if (!groupRow) return "—";
  if (nb(groupRow["Delivery Inspection Status"])) return "Delivery Inspection";
  const current = resolveJourney(groupRow, stageOverlay, featureFlags).find((s) => s.state === "current");
  return current?.label || "In progress";
}

export function escalationSnippet(esc) {
  const msg = String(esc?.triggeringMessage || "").trim();
  if (msg) return msg;
  return String(esc?.reasonText || "Escalated for review").trim();
}

export function pendingMemberCountForCard(row, derivedCount) {
  if (Number.isFinite(derivedCount)) return derivedCount;
  const count = Number(row?.["Members Not Joined Count"]);
  return Number.isFinite(count) ? count : 0;
}

export function customerNotJoinedForCard(row, derivedStatus) {
  return typeof derivedStatus === "boolean" ? derivedStatus : customerNotJoined(row);
}

export function cacheMemberSummary(summaries, rowId, summary) {
  if (!rowId || !summary) return summaries;
  return {
    ...summaries,
    [rowId]: {
      pendingMembers: summary.pendingMembers,
      customerNotJoined: !customerInGroup(summary.members),
    },
  };
}

export function shouldShowEscalationRecommendation({ escalation, openComplaints, hideEscalation }) {
  return !hideEscalation && escalation?.status === "open" && Number(openComplaints) > 0;
}

const notWired = () => [];

// `primary` buckets render as pills above the search/date controls; every
// other connected bucket renders as a row in the collapsible Filters list
// below them (see LeftRail.jsx).
export const BUCKETS = [
  { key: "all", label: "All Conversations", icon: Inbox, connected: true, primary: true, filter: allConversationsForMe },
  // Not built yet -- commented out rather than left visible-but-inert.
  // { key: "initiation_pending", label: "Initiation Pending", icon: CalendarClock, connected: false, filter: notWired },
  { key: "pending_invites", label: "Pending Invites", icon: UserPlus, connected: true, filter: pendingInvitesForMe },
  {
    key: "customer_not_joined",
    label: "Customer Not Joined",
    icon: UserX,
    danger: true,
    connected: true,
    filter: customerNotJoinedForMe,
  },
  {
    key: "unanswered",
    label: "Unanswered Messages",
    icon: MessageSquare,
    danger: true,
    connected: true,
    filter: unansweredForMe,
  },
  // Not built yet -- commented out rather than left visible-but-inert.
  // { key: "pending_response", label: "Pending Response", icon: MessageSquare, connected: false, filter: notWired },
  // { key: "customer_approval", label: "Customer Approval", icon: CheckCircle2, connected: false, filter: notWired },
  { key: "job_card_opened", label: "Job Card Opened", icon: FileText, connected: true, filter: jobCardOpenedForMe },
  { key: "job_card_closed", label: "Job Card Closed", icon: CheckCircle2, connected: true, filter: jobCardClosedForMe },
  {
    key: "escalations",
    label: "Escalations",
    icon: AlertTriangle,
    danger: true,
    connected: true,
    filter: escalatedGroupsForMe,
    kind: "escalations",
  },
];

// Land on everything rather than on one queue: the desk is where staff come to
// find a conversation, and the queues are a filter over it.
export const DEFAULT_BUCKET = "all";
